import { Stack, Text, Group, RangeSlider, Box } from '@mantine/core';
import { MIDNIGHT, SCHEDULE_DAY_OPTIONS, TIMELINE_COLORS } from 'constants/menuManagement';
import dayjs from 'dayjs';
import { memo } from 'react';
import {
  isDayClosed,
  timeToNumber,
  numberToTime,
  formatTimeLabel,
  getHourMarks,
  getHourLabels,
  resolvePrimaryId,
} from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/helpers';

import DaySwitch from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/StandardHours/DaySwitch';
import EditScheduleMenu from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/EditScheduleMenu';
import { ErrorCountBadge } from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/ErrorCountBadge';
import { ValidationError } from 'components/MenuManagement/MenuEdit/Hours/types';

type TimelineSchedule = {
  appliedDate?: string;
  dayIndex: string;
  endTime: string;
  isPeriod?: boolean;
  startTime: string;
  tempId?: string;
  uuid?: string;
};

type DaySwitchSchedule = {
  uuid: string;
  tempId?: string;
  startTime: string;
  endTime: string;
  dayIndex: string;
  isPeriod?: boolean;
  appliedDate: string;
};

type StandardTimelineProps = {
  schedules: TimelineSchedule[];
  handleToggle?: (id: string) => void;
  handle24Hours?: (id: string) => void;
  handleDeleteHours: (id: string) => void;
  handleAddPeriod?: (id: string) => void;
  handleTimeRangeChange: (id: string, times: [string, string]) => void;
  timelineColor?: string;
  getValidationErrors?: (dayIndex: string | number) => ValidationError[];
  showAddButton?: boolean;
};

// Normalizes data for DaySwitch component
function toDaySwitchSchedule(schedule: TimelineSchedule): DaySwitchSchedule {
  const defaultId = `default-${schedule.dayIndex}`;
  return {
    ...schedule,
    uuid: schedule.uuid || schedule.tempId || defaultId,
    tempId: schedule.tempId || (!schedule.uuid ? defaultId : undefined),
    dayIndex: schedule.dayIndex,
    appliedDate: dayjs().format('YYYY-MM-DD'),
  };
}

function addScheduleToDay(acc: Record<string, TimelineSchedule[]>, schedule: TimelineSchedule) {
  const dayKey = schedule.dayIndex.toString().toLowerCase();
  return {
    ...acc,
    [dayKey]: [...(acc[dayKey] || []), schedule],
  };
}

function addClosedScheduleIfMissing(grouped: Record<string, TimelineSchedule[]>, day: string) {
  const dayKey = day.toLowerCase();
  if (grouped[dayKey]) return grouped;

  return {
    ...grouped,
    [dayKey]: [
      {
        dayIndex: dayKey,
        startTime: MIDNIGHT,
        endTime: MIDNIGHT,
      },
    ],
  };
}

function groupSchedulesByDay(schedules: TimelineSchedule[]): Record<string, TimelineSchedule[]> {
  // Group all schedules including periods by day
  const grouped = schedules.reduce(addScheduleToDay, {});

  // If a day is missing when imported, add a default closed schedule
  return SCHEDULE_DAY_OPTIONS.reduce(
    (acc, day) => addClosedScheduleIfMissing(acc, day.value),
    grouped,
  );
}

const MemoizedHourLabels = memo(() => {
  const hourLabels = getHourLabels();
  return (
    <Box pos="relative" flex={1} h={20} ml={130}>
      {hourLabels.map(({ hour, displayHour }) => {
        const position = ((hour * 60) / 1440) * 100;
        return (
          <Text
            key={`hour-label-${hour}`}
            variant="caption-xs2"
            left={`${position}%`}
            w="nowrap"
            pos="absolute"
          >
            {displayHour}
          </Text>
        );
      })}
    </Box>
  );
});

export default function StandardTimeline({
  schedules,
  handleToggle,
  handle24Hours,
  handleDeleteHours,
  handleAddPeriod,
  handleTimeRangeChange,
  timelineColor = TIMELINE_COLORS.PICKUP,
  getValidationErrors,
  showAddButton = true,
}: StandardTimelineProps) {
  const schedulesByDay = groupSchedulesByDay(schedules);

  const hourMarks = getHourMarks();

  const handleTimeChange = (values: [number, number], schedule: TimelineSchedule) => {
    const scheduleId = resolvePrimaryId(schedule);

    const [startValue, endValue] = values;
    handleTimeRangeChange(scheduleId, [numberToTime(startValue), numberToTime(endValue)]);
  };

  return (
    <Stack gap="md">
      {/* Hour labels at the top */}
      <Group align="center">
        <MemoizedHourLabels />
        {/* This adds fixed spacing for icons */}
        <Box w={80} />
      </Group>

      {/* Schedule timeline */}
      {SCHEDULE_DAY_OPTIONS.map(({ value: dayKey, label: name }) => {
        const daySchedules = schedulesByDay[dayKey.toLowerCase()] || [];
        const primarySchedule = daySchedules.find(s => !s.isPeriod);
        const periodSchedules = daySchedules.filter(s => s.isPeriod);
        const dayErrors = getValidationErrors?.(dayKey) ?? [];

        if (!primarySchedule) return null;

        const isClosed = isDayClosed(primarySchedule);
        const primaryId = resolvePrimaryId(primarySchedule);

        const dayOption = {
          value: Number(primarySchedule.dayIndex),
          displayValue: name,
        };

        return (
          <Group key={dayKey} align="flex-start" gap="md" wrap="nowrap">
            <Group gap="xs2" w={110} align="center" wrap="nowrap" h="var(--dimension-lg)">
              {handleToggle && (
                <DaySwitch
                  primarySchedule={toDaySwitchSchedule(primarySchedule)}
                  primaryId={primaryId}
                  dayOption={dayOption}
                  hasSecondarySchedules={periodSchedules.length > 0}
                  handleToggle={handleToggle}
                />
              )}
            </Group>
            <Stack flex={1} gap="xs">
              {isClosed && (
                <Group align="center" wrap="nowrap">
                  <Box flex={1}>
                    <Box
                      h="var(--dimension-lg)"
                      bg="var(--surface-secondary)"
                      display="flex"
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Text variant="caption-xs">CLOSED</Text>
                    </Box>
                  </Box>
                  <Box w={80}>
                    <EditScheduleMenu
                      schedule={toDaySwitchSchedule(primarySchedule)}
                      schedules={daySchedules.map(toDaySwitchSchedule)}
                    />
                  </Box>
                </Group>
              )}
              {!isClosed && (
                <>
                  {/* Primary hours */}
                  <Group align="center" style={{ flexWrap: 'nowrap' }}>
                    <Box flex={1}>
                      <RangeSlider
                        key={primarySchedule.uuid || primarySchedule.tempId}
                        color={timelineColor}
                        min={0}
                        max={1439}
                        step={15}
                        // minRange is 14 (not 15) to allow the 11:45pm-11:59pm timeslot.
                        // This only affects the last slot; all other slots remain
                        // 15+ min due to step={15}.
                        minRange={14}
                        value={[
                          timeToNumber(primarySchedule.startTime),
                          timeToNumber(primarySchedule.endTime),
                        ]}
                        onChange={values => {
                          handleTimeChange(values, primarySchedule);
                        }}
                        label={formatTimeLabel}
                        labelAlwaysOn
                        marks={hourMarks}
                        styles={{
                          label: {
                            backgroundColor:
                              (dayErrors.some(error =>
                                error.periods.some(p => p.id === primaryId),
                              ) &&
                                'var(--content-caution)') ||
                              undefined,
                          },
                          thumb: { borderColor: timelineColor },
                          bar: {
                            cursor: 'grab',
                          },
                        }}
                      />
                    </Box>
                    {/* This adds fixed spacing for icons */}
                    <Box w={80}>
                      <Group gap="xs2" wrap="nowrap">
                        <EditScheduleMenu
                          schedule={toDaySwitchSchedule(primarySchedule)}
                          schedules={daySchedules.map(toDaySwitchSchedule)}
                          handle24Hours={() => handle24Hours?.(primaryId)}
                          handleDeleteHours={() => handleDeleteHours(primaryId)}
                          handleAddPeriod={() => handleAddPeriod?.(primaryId)}
                          disableAddPeriod={!showAddButton}
                        />
                        <ErrorCountBadge
                          validationErrors={dayErrors}
                          scheduleId={primaryId}
                          hasRollover={false}
                        />
                      </Group>
                    </Box>
                  </Group>

                  {/* Period hours */}
                  {periodSchedules.map(schedule => {
                    const scheduleId = resolvePrimaryId(schedule, true);
                    if (!scheduleId) return null;

                    const isPeriodInvalid = dayErrors.some(error =>
                      error.periods.some(period => period.id === scheduleId),
                    );

                    return (
                      <Group key={scheduleId} align="center" wrap="nowrap">
                        <Box flex={1}>
                          <RangeSlider
                            color={timelineColor}
                            min={0}
                            max={1439}
                            step={15}
                            // minRange is 14 (not 15) to allow the 11:45pm-11:59pm timeslot.
                            // See primary hours slider above for detailed explanation.
                            minRange={14}
                            value={[
                              timeToNumber(schedule.startTime),
                              timeToNumber(schedule.endTime),
                            ]}
                            onChange={values => handleTimeChange(values, schedule)}
                            label={formatTimeLabel}
                            labelAlwaysOn
                            marks={hourMarks}
                            styles={{
                              label: {
                                backgroundColor:
                                  (isPeriodInvalid && 'var(--content-caution)') || undefined,
                              },
                              thumb: { borderColor: timelineColor },
                              bar: {
                                cursor: 'grab',
                              },
                            }}
                          />
                        </Box>
                        <Box w={80}>
                          <Group gap="xs2" wrap="nowrap">
                            <EditScheduleMenu
                              schedule={toDaySwitchSchedule(schedule)}
                              schedules={daySchedules.map(toDaySwitchSchedule)}
                              handleDeleteHours={() => handleDeleteHours(scheduleId)}
                              disableAddPeriod={!showAddButton}
                            />
                            <ErrorCountBadge
                              validationErrors={dayErrors}
                              scheduleId={scheduleId}
                              hasRollover={false}
                            />
                          </Group>
                        </Box>
                      </Group>
                    );
                  })}
                </>
              )}
            </Stack>
          </Group>
        );
      })}
    </Stack>
  );
}
